import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Upload, Download } from 'lucide-react';
import { BACKEND_URL } from '../config';

interface PdfViewerProps {
  authenticated: boolean;
}

export default function PdfViewer({ authenticated }: PdfViewerProps) {
  const { i18n, t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [cvAvailable, setCvAvailable] = useState(false);

  const cvProxyUrl = `${BACKEND_URL}/api/cv/${i18n.language}`;

  const checkCvAvailability = () => {
    fetch(cvProxyUrl, { method: 'HEAD' })
      .then(r => setCvAvailable(r.ok))
      .catch(() => setCvAvailable(false));
  };

  useEffect(() => {
    checkCvAvailability();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const token = localStorage.getItem('token');
    if (!token) return;

    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('isFrench', String(i18n.language === 'fr'));

    try {
      const res = await fetch(`${BACKEND_URL}/api/upload-cv`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        // CV uploaded successfully — proxy route is now available
        setCvAvailable(true);
      } else {
        const err = await res.json().catch(() => ({}));
        console.error('Upload failed:', err);
      }
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen pt-28 pb-16 px-4">
      <div className="max-w-4xl mx-auto">
        <div>
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <h1 className="font-display font-extrabold tracking-[-0.04em] text-5xl sm:text-[64px] leading-none">{t('cv_label')}</h1>
            <div className="flex flex-wrap gap-2">
            {cvAvailable && (
              <a href={cvProxyUrl} download className="h-11 px-5 inline-flex items-center gap-2 rounded-full bg-ink text-base text-sm font-semibold hover:bg-primary hover:text-white transition-colors">
                <Download size={16} /> {t('download')}
              </a>
            )}
            {authenticated && (
              <label className={`h-11 flex items-center gap-2 px-5 rounded-full border border-zinc-700 text-sm text-zinc-300 hover:text-ink cursor-pointer transition-colors ${uploading ? 'opacity-50' : ''}`}>
                <Upload size={16} />
                {uploading ? '...' : `${t('upload_cv')} (${i18n.language.toUpperCase()})`}
                <input type="file" accept=".pdf" className="hidden" onChange={handleUpload} disabled={uploading} />
              </label>
            )}
            </div>
          </div>

          <div className="bg-surface rounded-3xl overflow-hidden">
            {cvAvailable ? (
              <>
                {/* Mobile: open in native viewer (iframe PDF has no reliable fit-to-width) */}
                <div className="flex flex-col items-center justify-center gap-4 py-16 px-6 text-center md:hidden">
                  <FileText size={40} className="text-primary-light/60" />
                  <p className="text-zinc-400 text-sm">{t('cv_mobile_hint') || 'Ouvrir le CV dans le visualiseur natif pour une meilleure lisibilité.'}</p>
                  <a
                    href={cvProxyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-11 inline-flex items-center gap-2 px-5 rounded-full bg-primary text-white font-semibold text-sm"
                  >
                    <FileText size={16} /> {t('open_cv') || 'Ouvrir le CV'}
                  </a>
                </div>
                {/* Desktop: embedded iframe */}
                <iframe
                  src={`${cvProxyUrl}#view=FitH`}
                  title="CV"
                  className="hidden md:block w-full"
                  style={{ height: '85vh' }}
                />
              </>
            ) : (
              <div className="flex items-center justify-center text-zinc-500 py-20">
                {t('cv_not_available')}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
